import { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button, Space } from 'antd';
import { ClearOutlined, SaveOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/hooks';

/**
 * SignaturePad component for drawing or uploading signatures
 * @param {Object} props
 * @param {Function} props.onSave - Callback when signature is saved (receives base64 PNG)
 * @param {Function} props.onClear - Callback when signature is cleared
 * @param {string} props.existingSignature - URL of existing signature image
 * @param {boolean} props.disabled - Whether the pad is disabled
 */
const SignaturePad = ({ onSave, onClear, existingSignature, disabled = false }) => {
  const { t } = useTranslation(['forms', 'messages']);
  const notification = useNotify();
  const sigPadRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(existingSignature || null);

  useEffect(() => {
    setPreviewUrl(existingSignature || null);
  }, [existingSignature]);

  const handleClear = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      setIsEmpty(true);
    }
    setPreviewUrl(null);
    onClear?.();
  };

  const handleSave = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      const dataUrl = sigPadRef.current.getCanvas().toDataURL('image/png');
      setPreviewUrl(dataUrl);
      onSave?.(dataUrl);
    } else {
      notification.warning({
        message: t('warning', { ns: 'messages' }),
        description: t('signatureEmpty') || 'Please draw a signature first',
      });
    }
  };

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: t('invalidImageFormat') || 'Please upload an image file',
      });
      return;
    }

    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: t('imageTooLarge') || 'Image must be less than 500KB',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setPreviewUrl(dataUrl);
      onSave?.(dataUrl);
    };
    reader.readAsDataURL(file);

    // Reset input
    event.target.value = '';
  };

  const handleBeginStroke = () => {
    setIsEmpty(false);
  };

  if (disabled && previewUrl) {
    return (
      <div className="signature-preview-container">
        <img
          src={previewUrl}
          alt="Signature"
          style={{
            maxWidth: '300px',
            height: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            padding: '10px',
            backgroundColor: '#fff',
          }}
        />
      </div>
    );
  }

  return (
    <div className="signature-pad-container">
      {previewUrl ? (
        <div className="signature-preview">
          <img
            src={previewUrl}
            alt="Signature Preview"
            style={{
              maxWidth: '300px',
              height: 'auto',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              padding: '10px',
              backgroundColor: '#fff',
              marginBottom: '10px',
            }}
          />
          {!disabled && (
            <div>
              <Button
                icon={<DeleteOutlined />}
                onClick={handleClear}
                danger
              >
                {t('removeSignature') || 'Remove Signature'}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              backgroundColor: '#fff',
              marginBottom: '10px',
            }}
          >
            <SignatureCanvas
              ref={sigPadRef}
              canvasProps={{
                width: 400,
                height: 150,
                className: 'signature-canvas',
                style: { width: '100%', height: '150px' },
              }}
              onBegin={handleBeginStroke}
              backgroundColor="white"
            />
          </div>

          <Space wrap>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClear}
              disabled={isEmpty}
            >
              {t('clear') || 'Clear'}
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              disabled={isEmpty}
            >
              {t('saveSignature') || 'Save Signature'}
            </Button>
            <Button
              icon={<UploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
            >
              {t('uploadImage') || 'Upload Image'}
            </Button>
          </Space>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            onChange={handleUpload}
            style={{ display: 'none' }}
          />
        </>
      )}
    </div>
  );
};

export default SignaturePad;
